import { useEffect, useRef, useState } from "react";
import { OrderBookLadder } from "../types/market";
import { API_BASE_URL } from "../lib/api";
import {
  isOrderbookProtocolV2Message,
  type PoolStateRefreshV2Message,
} from "@sportex100/shared/utils/orderbookState.ts";


export type OrderbookMessage = {
  type: "orderbook_update";
  protocol_version: 2;
  pool_state_version: number;
  season_id: number;
  season_team_id: number;
  pool_code: string;
  order: {
    trade_order_id: number;
    side: "BUY" | "SELL";
    price: number;
    quantity: number;
    status: string;
    trader_code?: string | null;
    participant_type?: 'HUMAN' | 'VIRTUAL' | null;
    participant_display_name?: string | null;
    participant_profile?: string | null;
    participant_profile_description?: string | null;
    pool_group_id?: number;
  };
  top_of_book: {
    bid: {
      trade_order_id: number;
      price: number;
      quantity: number;
      trader_code?: string | null;
      participant_type?: 'HUMAN' | 'VIRTUAL' | null;
      participant_display_name?: string | null;
      participant_profile?: string | null;
      participant_profile_description?: string | null;
      created_at?: string | null;
    } | null;
    ask: {
      trade_order_id: number;
      price: number;
      quantity: number;
      trader_code?: string | null;
      participant_type?: 'HUMAN' | 'VIRTUAL' | null;
      participant_display_name?: string | null;
      participant_profile?: string | null;
      participant_profile_description?: string | null;
      created_at?: string | null;
    } | null;
  };
  trades: Array<{
    trade_id: number;
    buy_order_id: number;
    sell_order_id: number;
    price: number;
    quantity: number;
    executed_at: string;
    buy_trader_code?: string | null;
    sell_trader_code?: string | null;
    buy_participant_type?: 'HUMAN' | 'VIRTUAL' | null;
    sell_participant_type?: 'HUMAN' | 'VIRTUAL' | null;
    buy_participant_display_name?: string | null;
    sell_participant_display_name?: string | null;
  }>;
  full_ladder?: OrderBookLadder;
};

export type PoolStateRefreshMessage = PoolStateRefreshV2Message;

export type OrderbookStreamMessage = OrderbookMessage | PoolStateRefreshMessage;

type UseOrderbookStreamArgs = {
  competitionCode: string;
  seasonCode: string;
  poolCode: string;
  onUpdate: (message: OrderbookStreamMessage) => void;
  acquireTicket?: () => Promise<string | null>;
  enabled?: boolean;
};

type StreamState = {
  status: "idle" | "connecting" | "ready" | "error";
  error?: string;
};

type SocketUrlArgs = Pick<UseOrderbookStreamArgs, "competitionCode" | "seasonCode" | "poolCode"> & {
  ticket?: string | null;
};

export const ORDERBOOK_WEBSOCKET_AUTH_CLOSE_CODE = 4401;

export const buildOrderbookSocketUrl = ({ competitionCode, seasonCode, poolCode, ticket }: SocketUrlArgs) => {
  // Use API_BASE_URL if set, otherwise fall back to current host
  let wsBase: string;
  if (API_BASE_URL) {
    // Convert https://api.sportex100.com to wss://api.sportex100.com
    wsBase = API_BASE_URL.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
  } else {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    wsBase = `${protocol}//${window.location.host}`;
  }
  const params = new URLSearchParams({
    competition_code: competitionCode,
    season_code: seasonCode,
    pool_code: poolCode,
    protocol: "2",
  });
  const normalizedTicket = ticket?.trim();
  if (normalizedTicket) {
    params.set("ticket", normalizedTicket);
  }
  return `${wsBase}/ws/orderbook?${params.toString()}`;
};

export function shouldReconnectOrderbookSocket({
  closeCode,
  manuallyClosed,
  isActive,
}: {
  closeCode?: number;
  manuallyClosed: boolean;
  isActive: boolean;
}) {
  if (!isActive || manuallyClosed) {
    return false;
  }

  return closeCode !== ORDERBOOK_WEBSOCKET_AUTH_CLOSE_CODE;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isOrderbookMessage(value: unknown): value is OrderbookMessage {
  return (
    isOrderbookProtocolV2Message(value) &&
    value.type === "orderbook_update" &&
    isRecord(value.order) &&
    Array.isArray(value.trades)
  );
}

export function useOrderbookStream({
  competitionCode,
  seasonCode,
  poolCode,
  onUpdate,
  acquireTicket,
  enabled = true,
}: UseOrderbookStreamArgs) {
  const [state, setState] = useState<StreamState>({ status: "idle" });
  const socketRef = useRef<WebSocket | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);

  useEffect(() => {
    if (!enabled || !competitionCode || !seasonCode || !poolCode) {
      return;
    }

    let isActive = true;
    let manuallyClosed = false;

    const clearRetryTimer = () => {
      if (retryTimeoutRef.current !== null) {
        window.clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (!isActive || manuallyClosed) {
        return;
      }
      const nextAttempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = nextAttempt;
      const delay = Math.min(1000 * 2 ** (nextAttempt - 1), 30000);
      clearRetryTimer();
      retryTimeoutRef.current = window.setTimeout(() => {
        if (!isActive || manuallyClosed) {
          return;
        }
        void connect();
      }, delay);
    };

    const connect = async () => {
      if (!isActive || manuallyClosed) {
        return;
      }

      setState({ status: "connecting" });
      let ticket: string | null = null;
      if (acquireTicket) {
        try {
          ticket = (await acquireTicket())?.trim() || null;
        } catch (error) {
          console.warn("Failed to acquire orderbook websocket ticket.", error);
        }
      }
      if (!isActive || manuallyClosed) {
        return;
      }
      if (acquireTicket && !ticket) {
        setState({ status: "error", error: "WebSocket authentication unavailable" });
        scheduleReconnect();
        return;
      }

      const url = buildOrderbookSocketUrl({ competitionCode, seasonCode, poolCode, ticket });
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        if (!isActive || manuallyClosed) {
          return;
        }
        reconnectAttemptsRef.current = 0;
        clearRetryTimer();
        setState({ status: "ready" });
      };

      socket.onerror = () => {
        if (!isActive || manuallyClosed) {
          return;
        }
        setState({ status: "error", error: "WebSocket error" });
      };

      socket.onclose = (event) => {
        if (
          !shouldReconnectOrderbookSocket({
            closeCode: event.code,
            manuallyClosed,
            isActive,
          })
        ) {
          if (event.code === ORDERBOOK_WEBSOCKET_AUTH_CLOSE_CODE && isActive && !manuallyClosed) {
            setState({ status: "error", error: "WebSocket authentication failed" });
          }
          return;
        }
        setState((prev) => (prev.status === "error" ? prev : { status: "error", error: "Connection closed" }));
        scheduleReconnect();
      };

      socket.onmessage = (event) => {
        try {
          const data: unknown = JSON.parse(event.data);
          if (isOrderbookMessage(data)) {
            onUpdate(data);
            return;
          }
          if (isOrderbookProtocolV2Message(data) && data.type === "pool_state_refresh") {
            onUpdate(data);
          }
        } catch (error) {
          console.error("Failed to process orderbook message", error);
        }
      };
    };

    void connect();

    return () => {
      isActive = false;
      manuallyClosed = true;
      clearRetryTimer();
      reconnectAttemptsRef.current = 0;

      const socket = socketRef.current;
      if (socket) {
        if (socket.readyState === WebSocket.CONNECTING) {
          const handleOpen = () => {
            socket.removeEventListener("open", handleOpen);
            socket.close(1000, "cleanup");
          };
          socket.addEventListener("open", handleOpen, { once: true });
        } else if (socket.readyState === WebSocket.OPEN) {
          socket.close(1000, "cleanup");
        } else if (socket.readyState === WebSocket.CLOSING) {
          socket.close();
        }
      }

      socketRef.current = null;
      setState({ status: "idle" });
    };
  }, [competitionCode, seasonCode, poolCode, onUpdate, acquireTicket, enabled]);

  return state;
}

export type OrderbookStreamState = StreamState;
